// Authentication entry point for the Flutter WebView host.
//
// The host cannot call into React, so login is exposed as a single global
// function: `AngkasAuth.login(jwt)`. It drives the pre-existing bridge contract
// (assign `__ANGKAS_JWT__`, then dispatch `angkas-jwt-ready`) so a token handed
// over by the host lands on exactly the same code path in tinode-web whether it
// arrives before or after mount.
//
// Login needs a WebSocket round trip, so the outcome cannot be returned to the
// host synchronously — `login()` only reports whether the token was accepted for
// processing. The real result follows as an `auth_result` message on the
// FlutterBridge channel. All host messages share the same payload shape:
//
//   {"type": "auth_result", "ok": true,  "reason": "AUTHENTICATED"}
//   {"type": "auth_result", "ok": false, "reason": "AUTH_REJECTED"}
//   {"type": "fatal_error", "reason": "CONNECTION_TIMEOUT"}
//   {"type": "session_expired", "reason": "SESSION_EXPIRED"}
//
// Exactly one `auth_result` is sent per accepted request.
//
// login() also re-authenticates in place: when a session has expired the app
// keeps its user id in React state, so "already authenticated" is decided by the
// SDK's own auth flag instead. A host that receives `session_expired` can hand
// over a fresh JWT and recover without reloading the WebView.

import { AUTH_TIMEOUT } from '../config.js';
import { bootLog, bootWarn } from './boot-diagnostics.js';
import { postToNativeHost } from './native-bridge.js';

/** Event that tells tinode-web a JWT is available in `globalThis.__ANGKAS_JWT__`. */
export const JWT_READY_EVENT = 'angkas-jwt-ready';

/**
 * `status` values in `auth_result` that this module owns. Failures reported by
 * the app itself use the CHAT_ERROR_REASON vocabulary from native-bridge.js.
 */
export const NATIVE_AUTH_STATUS = {
  // Login completed for this request.
  AUTHENTICATED: 'AUTHENTICATED',
  // A session was already live, so the supplied token was not used.
  ALREADY_AUTHENTICATED: 'ALREADY_AUTHENTICATED',
  // Argument was not a non-empty string; nothing was attempted.
  INVALID_TOKEN: 'INVALID_TOKEN',
  // Nothing reported an outcome before the deadline below.
  NO_RESPONSE: 'NO_RESPONSE',
  // Token was accepted but could not be handed to the app.
  FAILED: 'FAILED',
};

// Deliberately longer than the app's own AUTH_TIMEOUT so handleAuthTimeout()
// gets to report the accurate reason first. This deadline is only a backstop
// against the host waiting forever for a reply that no code path will send.
const RESULT_DEADLINE = AUTH_TIMEOUT + 5000;

let installed = false;
let awaitingResult = false;
let deadlineTimer = null;

function clearDeadline() {
  if (deadlineTimer) {
    clearTimeout(deadlineTimer);
    deadlineTimer = null;
  }
}

function postAuthResult(ok, status) {
  bootLog('auth:native result', {ok, reason: status});
  postToNativeHost({type: 'auth_result', ok, reason: status});
}

// Marks a request as in flight. Must run before the token is handed to the app,
// because dispatchEvent is synchronous and can settle the request immediately.
function awaitResult() {
  awaitingResult = true;
  clearDeadline();
  deadlineTimer = setTimeout(() => {
    deadlineTimer = null;
    bootWarn('auth:native no outcome before deadline', {deadlineMs: RESULT_DEADLINE});
    settle(false, NATIVE_AUTH_STATUS.NO_RESPONSE);
  }, RESULT_DEADLINE);
}

function settle(ok, status) {
  if (!awaitingResult) {
    return;
  }
  awaitingResult = false;
  clearDeadline();
  postAuthResult(ok, status);
}

/** Report a completed login to the host. No-op unless a request is in flight. */
export function settleNativeAuthSuccess(status) {
  settle(true, status || NATIVE_AUTH_STATUS.AUTHENTICATED);
}

/** Report a failed login to the host. No-op unless a request is in flight. */
export function settleNativeAuthFailure(status) {
  settle(false, status || NATIVE_AUTH_STATUS.FAILED);
}

/**
 * Authenticate with a JWT supplied by the native host.
 * Returns true when the token was accepted and an `auth_result` will follow.
 */
function login(jwt) {
  const token = typeof jwt === 'string' ? jwt.trim() : '';
  if (!token) {
    bootWarn('auth:native login rejected — expected a non-empty JWT string',
      {argType: typeof jwt});
    postAuthResult(false, NATIVE_AUTH_STATUS.INVALID_TOKEN);
    return false;
  }

  try {
    globalThis.__ANGKAS_JWT__ = token;
  } catch (err) {
    // Nothing can read the token, so the app would never attempt a login and
    // the host would wait out the whole deadline for no reason.
    bootWarn('auth:native could not assign globalThis.__ANGKAS_JWT__',
      {message: err instanceof Error ? err.message : String(err)});
    postAuthResult(false, NATIVE_AUTH_STATUS.FAILED);
    return false;
  }

  bootLog('auth:native login accepted', {tokenLength: token.length});
  awaitResult();

  try {
    globalThis.dispatchEvent(new Event(JWT_READY_EVENT));
  } catch (err) {
    // Only reachable before mount, where the bootstrap reads the global
    // directly, so the request is left in flight rather than failed.
    bootWarn(`auth:native dispatchEvent(${JWT_READY_EVENT}) failed`,
      {message: err instanceof Error ? err.message : String(err)});
  }
  return true;
}

/**
 * Install `globalThis.AngkasAuth`. Call at bootstrap before React mounts so the
 * function exists as early as possible and a pre-injected token is still
 * visible (the mount path consumes and clears it).
 */
export function initNativeAuthBridge() {
  if (typeof globalThis === 'undefined' || installed) {
    return;
  }
  installed = true;

  globalThis.AngkasAuth = Object.freeze({login});

  // A host using the older contract assigned __ANGKAS_JWT__ directly and never
  // called login(), so adopt that token as a request to make sure it still gets
  // an auth_result.
  if ((globalThis.__ANGKAS_JWT__ || '').trim()) {
    bootLog('auth:native adopting pre-injected __ANGKAS_JWT__');
    awaitResult();
  }

  bootLog('auth:native bridge installed', {
    hasFlutterBridge: typeof globalThis.FlutterBridge?.postMessage === 'function',
  });
}
